import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { NewEvaluationDialog } from '@/components/admin/NewEvaluationDialog';
import {
  ArrowLeft, Loader2, Send, PenLine, Wrench, Star, History,
  Layers, Plus, CheckCircle2, Undo2, Archive,
} from 'lucide-react';

const STATUS_TONE = {
  draft: 'bg-muted text-muted-foreground',
  submitted_for_review: 'bg-warning/10 text-warning border-warning/30',
  needs_revision: 'bg-destructive/10 text-destructive border-destructive/30',
  active: 'bg-success/10 text-success border-success/30',
  retired: 'bg-muted text-muted-foreground line-through',
};

const LANGS = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
];

const REPORT_TEMPLATES = ['standard', 'talentcheck-psych', 'competency-benchmark'];

export default function AdminAssessmentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState('overview');
  const [levels, setLevels] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCfgId, setAssignCfgId] = useState(null);
  const [cfgDialog, setCfgDialog] = useState(null); // {mode:'create'|'edit', cfg}
  const [reasonDialog, setReasonDialog] = useState(null); // {cfg}
  const [historyDialog, setHistoryDialog] = useState(null); // cfg

  const load = useCallback(() => {
    setProfile((prev) => { if (!prev) setLoading(true); return prev; });
    adminApi.getAssessmentProfile(id)
      .then(setProfile)
      .catch((e) => toast.error(t('toasts.loadFailed'), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, [id, t]);
  useEffect(load, [load]);
  useEffect(() => { adminApi.getLibraryVocab({ field: 'level' }).then((v) => setLevels(Array.isArray(v) ? v : v?.level || [])).catch(() => {}); }, []);

  const taxPath = useMemo(() => (profile?.taxonomy_path || [])
    .map((l) => (isEN ? (l?.en || l?.es) : (l?.es || l?.en))).filter(Boolean).join(' / '), [profile, isEN]);

  const transition = async (cfg, action, reason = null) => {
    try {
      await adminApi.transitionAssessmentConfig(cfg.id, action, reason);
      toast.success(t('library.profile.cfg.transitionOk'));
      load();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
  };
  const setDefault = async (cfg) => {
    try {
      await adminApi.setDefaultAssessmentConfig(cfg.id);
      toast.success(t('library.profile.cfg.defaultOk'));
      load();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
  };

  if (loading) return (
    <><Topbar title={t('library.profile.loading')} />
      <div className="p-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('library.profile.loading')}
      </div></>
  );
  if (!profile) return (
    <><Topbar title={t('library.profile.notFound')} />
      <div className="p-12 text-center">
        <Button onClick={() => navigate('/admin/questionnaires')} className="gap-1.5" data-testid="ap-back-btn">
          <ArrowLeft className="h-4 w-4" /> {t('library.profile.backToLibrary')}
        </Button>
      </div></>
  );

  const configs = profile.configurations || [];
  const activeConfigs = configs.filter((c) => c.status === 'active');

  return (
    <>
      <Topbar title={tData(profile.name)} subtitle={t('library.profile.subtitle')} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="assessment-profile-page">
        {/* Header row */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/questionnaires')} className="gap-1.5" data-testid="ap-back-btn">
            <ArrowLeft className="h-4 w-4" /> {t('library.profile.backToLibrary')}
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => navigate(`/admin/questionnaires/${id}`)} className="gap-1.5" data-testid="ap-technical-view-btn">
              <Wrench className="h-4 w-4" /> {t('library.profile.technicalView')}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/admin/studio/edit/${id}`)} className="gap-1.5" data-testid="ap-edit-studio-btn">
              <PenLine className="h-4 w-4" /> {t('library.profile.editInStudio')}
            </Button>
            <Button onClick={() => { setAssignCfgId(null); setAssignOpen(true); }} className="bg-gradient-primary gap-1.5" data-testid="ap-assign-btn">
              <Send className="h-4 w-4" /> {t('library.profile.assign')}
            </Button>
          </div>
        </div>

        {/* Identity card */}
        <Card className="p-6 shadow-card" data-testid="ap-identity-card">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <code className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">{profile.display_code || profile.code}</code>
            <Badge variant="outline" className="text-xs" data-testid="ap-status-badge">
              {t(`library.lifecycle.statuses.${profile.status}`, { defaultValue: profile.status })}
            </Badge>
            {profile.assessment_type && (
              <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
                {t(`library.search.types.${profile.assessment_type}`, { defaultValue: profile.assessment_type })}
              </Badge>
            )}
            {profile.version_number && <Badge variant="outline" className="text-xs">v{profile.version_number}</Badge>}
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{tData(profile.name)}</h1>
          {taxPath && <p className="text-xs text-muted-foreground mt-1" data-testid="ap-taxonomy-path">{taxPath}</p>}
          {profile.description && <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{tData(profile.description)}</p>}
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="ap-tabs">
            <TabsTrigger value="overview" data-testid="ap-tab-overview">{t('library.profile.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="content" data-testid="ap-tab-content">{t('library.profile.tabs.content')}</TabsTrigger>
            <TabsTrigger value="configurations" data-testid="ap-tab-configurations">
              {t('library.profile.tabs.configurations')} <Badge variant="outline" className="ml-1.5 text-xs">{configs.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="competencies" data-testid="ap-tab-competencies">{t('library.profile.tabs.competencies')}</TabsTrigger>
            <TabsTrigger value="usage" data-testid="ap-tab-usage">{t('library.profile.tabs.usage')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <OverviewTab profile={profile} t={t} isEN={isEN} tData={tData} />
          </TabsContent>

          <TabsContent value="content" className="mt-4">
            <ContentTab profile={profile} t={t} tData={tData} />
          </TabsContent>

          <TabsContent value="configurations" className="mt-4">
            <Card className="shadow-card overflow-hidden" data-testid="ap-configs-card">
              <div className="p-4 flex items-center justify-between border-b border-border">
                <div>
                  <h3 className="font-semibold text-sm">{t('library.profile.cfg.title')}</h3>
                  <p className="text-xs text-muted-foreground">{t('library.profile.cfg.hint')}</p>
                </div>
                <Button size="sm" onClick={() => setCfgDialog({ mode: 'create', cfg: null })} className="gap-1.5 bg-gradient-primary" data-testid="ap-cfg-new-btn">
                  <Plus className="h-4 w-4" /> {t('library.profile.cfg.new')}
                </Button>
              </div>
              {configs.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">{t('library.profile.cfg.empty')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('library.profile.cfg.label')}</TableHead>
                      <TableHead>{t('library.profile.cfg.language')}</TableHead>
                      <TableHead>{t('library.profile.cfg.level')}</TableHead>
                      <TableHead>{t('library.profile.cfg.reportTemplate')}</TableHead>
                      <TableHead>{t('library.profile.cfg.suites')}</TableHead>
                      <TableHead>{t('library.profile.cfg.status')}</TableHead>
                      <TableHead className="text-right">{t('library.profile.cfg.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {configs.map((c) => (
                      <ConfigRow key={c.id} cfg={c} t={t} isAdmin={isAdmin}
                        onEdit={() => setCfgDialog({ mode: 'edit', cfg: c })}
                        onSubmit={() => transition(c, 'submit')}
                        onApprove={() => transition(c, 'approve')}
                        onRequestChanges={() => setReasonDialog({ cfg: c })}
                        onRetire={() => transition(c, 'retire')}
                        onSetDefault={() => setDefault(c)}
                        onHistory={() => setHistoryDialog(c)}
                        onAssign={() => { setAssignCfgId(c.id); setAssignOpen(true); }}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="px-4 py-3 text-xs text-muted-foreground border-t border-border" data-testid="ap-cfg-assign-note">
                {t('library.profile.cfg.assignNote')}
              </p>
            </Card>
          </TabsContent>

          <TabsContent value="competencies" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ap-competencies-card">
              {(profile.competencies_measured || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">{t('library.profile.comp.empty')}</p>
              ) : (
                <div className="space-y-2">
                  {profile.competencies_measured.map((c) => (
                    <div key={c.code} className="flex items-center gap-3" data-testid={`ap-comp-${c.code}`}>
                      <button className="text-sm font-medium text-primary hover:underline" onClick={() => navigate('/admin/competencies')}>
                        {c.name}
                      </button>
                      {c.category && <Badge variant="outline" className="text-xs">{c.category}</Badge>}
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden max-w-xs">
                        <div className="h-full bg-primary" style={{ width: `${Math.round((c.weight || 0) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-12 text-right">{Math.round((c.weight || 0) * 100)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="usage" className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="ap-usage-cards">
              <UsageStat icon={Send} label={t('library.profile.usage.timesAssigned')} value={profile.usage?.times_assigned ?? 0} />
              <UsageStat icon={Layers} label={t('library.profile.usage.companies')} value={profile.usage?.companies_count ?? 0} />
              <UsageStat icon={CheckCircle2} label={t('library.profile.usage.completed')} value={profile.usage?.completed_count ?? 0} />
            </div>
            <p className="text-xs text-muted-foreground mt-3">{t('library.profile.usage.aggregateNote')}</p>
          </TabsContent>
        </Tabs>
      </div>

      <NewEvaluationDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        defaultQuestionnaireId={id}
        defaultQuestionnaireLabel={tData(profile.name)}
        defaultConfigurationId={assignCfgId}
        onCreated={() => { setAssignOpen(false); load(); }}
      />

      {cfgDialog && (
        <ConfigDialog
          state={cfgDialog}
          onClose={() => setCfgDialog(null)}
          onSaved={() => { setCfgDialog(null); load(); }}
          assessmentId={id}
          levels={levels}
          availableSuites={profile.available_suites || []}
          isAdmin={isAdmin}
          t={t}
        />
      )}

      {reasonDialog && (
        <ReasonDialog
          onClose={() => setReasonDialog(null)}
          onConfirm={(reason) => { transition(reasonDialog.cfg, 'request_changes', reason); setReasonDialog(null); }}
          t={t}
        />
      )}

      {historyDialog && (
        <HistoryDialog cfg={historyDialog} onClose={() => setHistoryDialog(null)} t={t} />
      )}
    </>
  );
}

function OverviewTab({ profile, t, isEN, tData }) {
  const uses = (profile.recommended_uses || [])
    .map((u) => (isEN ? (u.label_i18n?.en || u.label_i18n?.es) : (u.label_i18n?.es || u.label_i18n?.en)))
    .filter(Boolean);
  const rows = [
    [t('library.profile.ov.objective'), profile.objective],
    [t('library.profile.ov.instructions'), profile.instructions],
    [t('library.profile.ov.difficulty'), profile.difficulty],
    [t('library.profile.ov.duration'), profile.estimated_minutes ? `${profile.estimated_minutes} min` : null],
    [t('library.profile.ov.passingScore'), profile.passing_score != null ? `${profile.passing_score}` : null],
    [t('library.profile.ov.author'), profile.author_display],
    [t('library.profile.ov.reviewer'), profile.reviewer_display],
    [t('library.profile.ov.published'), profile.published_at ? new Date(profile.published_at).toLocaleDateString(getUiLocale()) : null],
  ].filter(([, v]) => v);
  return (
    <Card className="p-5 shadow-card space-y-4" data-testid="ap-overview-card">
      {rows.length === 0 && uses.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-6">{t('library.profile.ov.empty')}</p>
      )}
      {rows.map(([k, v]) => (
        <div key={k}>
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{k}</p>
          <p className="text-sm mt-0.5 whitespace-pre-wrap">{typeof v === 'string' ? tData(v) : v}</p>
        </div>
      ))}
      {uses.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{t('library.profile.ov.recommendedUses')}</p>
          <div className="flex flex-wrap gap-1.5">
            {uses.map((u) => <Badge key={u} variant="outline" className="text-xs">{u}</Badge>)}
          </div>
        </div>
      )}
    </Card>
  );
}

function ContentTab({ profile, t, tData }) {
  const outline = profile.content_outline || [];
  return (
    <Card className="p-5 shadow-card" data-testid="ap-content-card">
      <p className="text-xs text-muted-foreground mb-4">{t('library.profile.content.hint')}</p>
      {outline.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">{t('library.profile.content.empty')}</p>
      ) : (
        <div className="space-y-3">
          {outline.map((s, i) => (
            <div key={s.id || i} className="border border-border rounded-lg p-3" data-testid={`ap-section-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold">{s.title ? tData(s.title) : t('library.profile.content.unsectioned')}</p>
                <Badge variant="outline" className="text-xs">{s.question_count} {t('library.profile.content.questions')}</Badge>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.entries(s.type_mix || {}).map(([type, n]) => (
                  <Badge key={type} variant="outline" className="text-xs bg-muted/40">{type} × {n}</Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ConfigRow({ cfg, t, isAdmin, onEdit, onSubmit, onApprove, onRequestChanges, onRetire, onSetDefault, onHistory, onAssign }) {
  const sc = cfg.suite_compatibility || {};
  const canEdit = ['draft', 'needs_revision'].includes(cfg.status) || (cfg.status === 'active' && isAdmin);
  return (
    <TableRow data-testid={`ap-cfg-row-${cfg.id}`}>
      <TableCell>
        <div className="flex items-center gap-1.5">
          {cfg.is_default && <Star className="h-3.5 w-3.5 text-warning fill-warning" data-testid={`ap-cfg-default-${cfg.id}`} />}
          <span className="font-medium text-sm">{cfg.label}</span>
        </div>
        <span className="text-xs text-muted-foreground">v{cfg.semver}</span>
      </TableCell>
      <TableCell className="text-xs uppercase">{cfg.language}</TableCell>
      <TableCell className="text-xs">{cfg.level || '—'}</TableCell>
      <TableCell className="text-xs">{cfg.report_template}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {(sc.suites || []).map((s) => (
            <Badge key={s} variant="outline" className={`text-xs ${sc.mode === 'exclusive' ? 'border-warning/40 text-warning' : ''}`}>
              {sc.mode === 'exclusive' ? '🔒 ' : ''}{s}
            </Badge>
          ))}
          {(sc.suites || []).length === 0 && <span className="text-xs text-muted-foreground">{t('library.profile.cfg.allSuites')}</span>}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-xs ${STATUS_TONE[cfg.status] || ''}`} data-testid={`ap-cfg-status-${cfg.id}`}>
          {t(`library.profile.cfg.statuses.${cfg.status}`, { defaultValue: cfg.status })}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          {canEdit && <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`ap-cfg-edit-${cfg.id}`}>{t('library.profile.cfg.edit')}</Button>}
          {['draft', 'needs_revision'].includes(cfg.status) && (
            <Button size="sm" variant="ghost" onClick={onSubmit} data-testid={`ap-cfg-submit-${cfg.id}`}>{t('library.profile.cfg.submit')}</Button>
          )}
          {isAdmin && ['submitted_for_review', 'draft'].includes(cfg.status) && (
            <Button size="sm" variant="ghost" className="text-success" onClick={onApprove} data-testid={`ap-cfg-approve-${cfg.id}`}>
              {t('library.profile.cfg.approve')}
            </Button>
          )}
          {isAdmin && cfg.status === 'submitted_for_review' && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={onRequestChanges} data-testid={`ap-cfg-changes-${cfg.id}`}>
              <Undo2 className="h-3.5 w-3.5 mr-1" />{t('library.profile.cfg.requestChanges')}
            </Button>
          )}
          {isAdmin && cfg.status === 'active' && !cfg.is_default && (
            <Button size="sm" variant="ghost" onClick={onSetDefault} data-testid={`ap-cfg-setdefault-${cfg.id}`}>
              <Star className="h-3.5 w-3.5 mr-1" />{t('library.profile.cfg.setDefault')}
            </Button>
          )}
          {isAdmin && cfg.status === 'active' && (
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={onRetire} data-testid={`ap-cfg-retire-${cfg.id}`}>
              <Archive className="h-3.5 w-3.5 mr-1" />{t('library.profile.cfg.retire')}
            </Button>
          )}
          {cfg.status === 'active' && (
            <Button size="sm" variant="ghost" className="text-primary" onClick={onAssign} data-testid={`ap-cfg-assign-${cfg.id}`}>
              <Send className="h-3.5 w-3.5 mr-1" />{t('library.profile.cfg.assignRow')}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onHistory} data-testid={`ap-cfg-history-${cfg.id}`}>
            <History className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ConfigDialog({ state, onClose, onSaved, assessmentId, levels, availableSuites, isAdmin, t }) {
  const editing = state.mode === 'edit';
  const cfg = state.cfg;
  const activeLabelOnly = editing && cfg.status === 'active';
  const [form, setForm] = useState(() => editing ? {
    label: cfg.label || '', language: cfg.language || 'es', level: cfg.level || '',
    semver: cfg.semver || '1.0', report_template: cfg.report_template || 'standard',
    mode: cfg.suite_compatibility?.mode || 'shared',
    suites: cfg.suite_compatibility?.suites || [],
  } : { label: '', language: 'es', level: '', semver: '1.0', report_template: 'standard', mode: 'shared', suites: [] });
  const [saving, setSaving] = useState(false);

  const toggleSuite = (code) => setForm((f) => ({
    ...f, suites: f.suites.includes(code) ? f.suites.filter((s) => s !== code) : [...f.suites, code],
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const patch = activeLabelOnly
          ? { label: form.label }
          : {
            label: form.label || undefined, language: form.language, level: form.level || null,
            semver: form.semver, report_template: form.report_template,
            suite_compatibility: { mode: form.mode, suites: form.suites },
          };
        await adminApi.updateAssessmentConfig(cfg.id, patch);
      } else {
        await adminApi.createAssessmentConfig(assessmentId, {
          label: form.label || undefined, language: form.language, level: form.level || null,
          semver: form.semver, report_template: form.report_template,
          suite_compatibility: { mode: form.mode, suites: form.suites },
        });
      }
      toast.success(t('library.profile.cfg.savedOk'));
      onSaved();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ap-cfg-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? t('library.profile.cfg.editTitle') : t('library.profile.cfg.newTitle')}</DialogTitle>
        </DialogHeader>
        {!editing && <p className="text-xs text-muted-foreground -mt-2">{t('library.profile.cfg.draftHint')}</p>}
        {activeLabelOnly && <p className="text-xs text-warning -mt-2">{t('library.profile.cfg.activeLabelOnly')}</p>}
        <div className="space-y-3 py-1">
          <div>
            <Label>{t('library.profile.cfg.label')}</Label>
            <Input data-testid="ap-cfg-form-label" className="mt-1" value={form.label} placeholder={t('library.profile.cfg.labelPh')} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          {!activeLabelOnly && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('library.profile.cfg.language')}</Label>
                  <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                    <SelectTrigger data-testid="ap-cfg-form-language" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('library.profile.cfg.level')}</Label>
                  <Select value={form.level || '__none__'} onValueChange={(v) => setForm({ ...form, level: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="ap-cfg-form-level" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {levels.map((l) => <SelectItem key={l.code} value={l.code}>{l.label_es || l.label_en || l.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('library.profile.cfg.semver')}</Label>
                  <Input data-testid="ap-cfg-form-semver" className="mt-1" value={form.semver} onChange={(e) => setForm({ ...form, semver: e.target.value })} />
                </div>
                <div>
                  <Label>{t('library.profile.cfg.reportTemplate')}</Label>
                  <Select value={form.report_template} onValueChange={(v) => setForm({ ...form, report_template: v })}>
                    <SelectTrigger data-testid="ap-cfg-form-template" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{REPORT_TEMPLATES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('library.profile.cfg.compatibility')}</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="ap-cfg-form-mode" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">{t('library.profile.cfg.shared')}</SelectItem>
                    <SelectItem value="exclusive">{t('library.profile.cfg.exclusive')}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-1.5 mt-2 max-h-36 overflow-y-auto border border-border rounded-md p-2">
                  {availableSuites.map((s) => (
                    <label key={s.code} className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox checked={form.suites.includes(s.code)} onCheckedChange={() => toggleSuite(s.code)} data-testid={`ap-cfg-suite-${s.code}`} />
                      {s.name || s.code}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t('library.profile.cfg.cancel')}</Button>
          <Button onClick={save} disabled={saving} className="bg-gradient-primary" data-testid="ap-cfg-form-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {editing ? t('library.profile.cfg.save') : t('library.profile.cfg.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonDialog({ onClose, onConfirm, t }) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ap-reason-dialog">
        <DialogHeader><DialogTitle>{t('library.profile.cfg.requestChangesTitle')}</DialogTitle></DialogHeader>
        <div className="py-1">
          <Label>{t('library.profile.cfg.reason')}</Label>
          <Textarea data-testid="ap-reason-input" className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t('library.profile.cfg.cancel')}</Button>
          <Button disabled={!reason.trim()} onClick={() => onConfirm(reason.trim())} data-testid="ap-reason-confirm">
            {t('library.profile.cfg.requestChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ cfg, onClose, t }) {
  const entries = [...(cfg.review_history || [])].reverse();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ap-history-dialog">
        <DialogHeader><DialogTitle>{t('library.profile.cfg.historyTitle')}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto py-1">
          {entries.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t('library.profile.cfg.historyEmpty')}</p>}
          {entries.map((h, i) => (
            <div key={i} className="border border-border rounded-md p-2.5 text-xs" data-testid={`ap-history-entry-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{t(`library.profile.cfg.actions_history.${h.action}`, { defaultValue: h.action })}</span>
                <span className="text-muted-foreground">{h.at ? new Date(h.at).toLocaleString(getUiLocale()) : ''}</span>
              </div>
              <p className="text-muted-foreground mt-0.5">{h.by_name}{h.by_role ? ` · ${h.by_role}` : ''}</p>
              {h.comment && <p className="mt-1 italic">"{h.comment}"</p>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsageStat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 shadow-card flex items-center gap-4">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <p className="text-2xl font-bold font-display">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}
